import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { SUPPLIER_DOCUMENT_TYPE_LABELS, type SupplierDocumentType } from "@/components/suppliers/documents/supplierDocumentTypes";

const BUCKET = "supplier-documents";

interface DocRow {
  id: string;
  document_type: SupplierDocumentType;
  file_name: string;
  storage_path: string;
  mime_type: string | null;
  supplier_id: string;
}

const ViewSupplierDocument: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [doc, setDoc] = useState<DocRow | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdBlobUrl: string | null = null;
    const load = async () => {
      if (!documentId) return;
      setLoading(true);
      setError(null);
      try {
        const { data, error: dbErr } = await supabase
          .from("supplier_documents")
          .select("id, document_type, file_name, storage_path, mime_type, supplier_id")
          .eq("id", documentId)
          .maybeSingle();
        if (dbErr) throw dbErr;
        if (!data) {
          if (!cancelled) setError("Document not found or you do not have access.");
          return;
        }
        const { data: urlData, error: urlErr } = await supabase.storage
          .from(BUCKET)
          .createSignedUrl(data.storage_path, 60 * 10);
        if (urlErr || !urlData) throw urlErr ?? new Error("Could not generate preview link");

        // Fetch the file and render from a same-origin blob: URL so Chrome's
        // PDF viewer / extensions don't block the cross-origin embed.
        let localBlobUrl: string | null = null;
        try {
          const res = await fetch(urlData.signedUrl);
          if (res.ok) {
            const raw = await res.blob();
            const typed = data.mime_type
              ? new Blob([raw], { type: data.mime_type })
              : raw;
            localBlobUrl = URL.createObjectURL(typed);
            createdBlobUrl = localBlobUrl;
          }
        } catch {
          // fall back to the signed URL if the fetch fails
        }

        if (!cancelled) {
          setDoc(data as DocRow);
          setSignedUrl(urlData.signedUrl);
          setBlobUrl(localBlobUrl);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load document.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [documentId]);

  const handleDownload = async () => {
    if (!doc) return;
    const { data, error: urlErr } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(doc.storage_path, 60, { download: doc.file_name });
    if (urlErr || !data) {
      notify.error(urlErr?.message ?? "Could not generate download link");
      return;
    }
    const a = document.createElement("a");
    a.href = data.signedUrl;
    a.download = doc.file_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isPdf = doc?.mime_type === "application/pdf" || doc?.file_name.toLowerCase().endsWith(".pdf");
  const isImage = doc?.mime_type?.startsWith("image/");

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              doc?.supplier_id
                ? navigate(`/cost-center/suppliers/${doc.supplier_id}`)
                : navigate(-1)
            }
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {doc && (
                <Badge variant="outline">
                  {SUPPLIER_DOCUMENT_TYPE_LABELS[doc.document_type]}
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium truncate">{doc?.file_name ?? "Loading…"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {signedUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={signedUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in new tab
              </a>
            </Button>
          )}
          <Button size="sm" onClick={handleDownload} disabled={!doc}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      <div className="flex-1 bg-muted/30 min-h-0">
        {loading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading document…
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
            {error}
          </div>
        ) : (blobUrl || signedUrl) && isPdf ? (
          <iframe
            src={blobUrl ?? signedUrl ?? ""}
            title={doc?.file_name ?? "Document"}
            className="w-full h-full border-0"
          />
        ) : (blobUrl || signedUrl) && isImage ? (
          <div className="h-full w-full flex items-center justify-center p-4 overflow-auto">
            <img
              src={blobUrl ?? signedUrl ?? ""}
              alt={doc?.file_name ?? "Document"}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <p>Preview not available for this file type.</p>
            <Button size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download to view
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewSupplierDocument;
